import type { ReportFilters } from "../lib/reportFilters";
import { SEVERITY_OPTIONS, STATUS_OPTIONS } from "../lib/reportFilters";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { cn } from "../lib/utils";

interface ReportFilterControlsProps {
  filters: ReportFilters;
  platforms: string[];
  versions: string[];
  onChange: <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => void;
  /** "inline" lays the controls in a horizontal row; "stacked" makes the
   *  FilterRow blocks vertical (used inside the mobile filter sheet). */
  layout?: "inline" | "stacked";
  className?: string;
}

/**
 * ReportFilterControls renders the four canonical filter chips
 * (Status / Severity / Platform / Version) for the reports list. The
 * status and severity chips always appear; platform and version chips
 * only render when the underlying report list actually has values for
 * them, so the user never sees a one-item picker for an empty column.
 *
 * The component is shared between the desktop filter bar and the mobile
 * filter sheet — only the layout (inline vs stacked) changes.
 */
export function ReportFilterControls({
  filters,
  platforms,
  versions,
  onChange,
  layout = "inline",
  className,
}: ReportFilterControlsProps) {
  const isStacked = layout === "stacked";
  return (
    <div
      className={cn(
        isStacked
          ? "flex flex-col gap-5"
          : "flex flex-wrap items-center gap-x-4 gap-y-2",
        className,
      )}
    >
      <FilterRow label="Status" stacked={isStacked}>
        <ToggleGroup
          value={filters.status ? [filters.status] : []}
          onValueChange={(value: string[]) => onChange("status", (value[0] ?? "") as ReportFilters["status"])}
          aria-label="Status filter"
          spacing={4}
          className={isStacked ? "flex-wrap" : undefined}
        >
          {STATUS_OPTIONS.map((s) => (
            <ToggleGroupItem
              key={s}
              value={s}
              aria-label={s}
              className="capitalize"
            >
              {s.replace("_", " ")}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </FilterRow>
      <FilterRow label="Severity" stacked={isStacked}>
        <ToggleGroup
          value={filters.severity ? [filters.severity] : []}
          onValueChange={(value: string[]) => onChange("severity", (value[0] ?? "") as ReportFilters["severity"])}
          aria-label="Severity filter"
          spacing={4}
          className={isStacked ? "flex-wrap" : undefined}
        >
          {SEVERITY_OPTIONS.map((s) => (
            <ToggleGroupItem key={s} value={s} aria-label={s}>
              {s}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </FilterRow>
      {platforms.length > 0 ? (
        <FilterRow label="Platform" stacked={isStacked}>
          <ToggleGroup
            value={filters.platform ? [filters.platform] : []}
            onValueChange={(value: string[]) => onChange("platform", (value[0] ?? "") as ReportFilters["platform"])}
            aria-label="Platform filter"
            spacing={4}
            className={isStacked ? "flex-wrap" : undefined}
          >
            {platforms.map((p) => (
              <ToggleGroupItem key={p} value={p} aria-label={p}>
                {p}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </FilterRow>
      ) : null}
      {versions.length > 0 ? (
        <FilterRow label="Version" stacked={isStacked}>
          <ToggleGroup
            value={filters.gameVersion ? [filters.gameVersion] : []}
            onValueChange={(value: string[]) => onChange("gameVersion", (value[0] ?? "") as ReportFilters["gameVersion"])}
            aria-label="Version filter"
            spacing={4}
            className={isStacked ? "flex-wrap" : undefined}
          >
            {versions.map((v) => (
              <ToggleGroupItem key={v} value={v} aria-label={v}>
                {v}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </FilterRow>
      ) : null}
    </div>
  );
}

function FilterRow({
  label,
  children,
  stacked,
}: {
  label: string;
  children: React.ReactNode;
  stacked?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3",
        stacked && "flex-col items-start gap-2",
      )}
    >
      <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}
